import { z } from 'zod';

export const PaymentMethod = z.enum([
  'cash',
  'mobile_money',
  'bank_transfer',
  'auto_debit',
  'evc_plus',
  'zaad',
  'sahal',
  'mpesa',
]);
export type PaymentMethodType = z.infer<typeof PaymentMethod>;

export const PaymentType = z.enum([
  'down_payment',
  'excess',
  'installment',
  'early_payoff',
  'penalty',
]);
export type PaymentTypeT = z.infer<typeof PaymentType>;

/**
 * A discriminator the backend omitted, or one this build does not know yet.
 *
 * Deliberately not a member of `PaymentType` — it is never written, only read.
 * Folding an unrecognised row into `installment` is the exact mislabel the Type
 * column exists to prevent: a deposit rendered as servicing money tells an
 * operator the customer started repaying before the schedule began.
 */
export const UNKNOWN_PAYMENT_TYPE = 'unknown';

/** Read-side discriminator: a known type, or the explicit unknown sentinel. */
export const ResolvedPaymentType = z.union([
  PaymentType,
  z.literal(UNKNOWN_PAYMENT_TYPE),
]);
export type ResolvedPaymentTypeT = z.infer<typeof ResolvedPaymentType>;

/**
 * Narrow a raw backend value to a discriminator without ever guessing a real
 * one. Anything unparseable — missing, null, a type added server-side after
 * this build shipped — surfaces as `unknown` so the row reads as unclassified
 * rather than as money it is not.
 */
export const resolvePaymentType = (value: unknown): ResolvedPaymentTypeT => {
  const parsed = PaymentType.safeParse(value);
  return parsed.success ? parsed.data : UNKNOWN_PAYMENT_TYPE;
};

/** Settlement state of a single payment, mirrors the backend `PaymentStatus`. */
export const PaymentStatus = z.enum([
  'pending',
  'completed',
  'failed',
  'reversed',
]);
export type PaymentStatusT = z.infer<typeof PaymentStatus>;

/** A settlement state this build does not know, or the backend omitted. */
export const UNKNOWN_PAYMENT_STATUS = 'unknown';

/** Read-side status: a known state, or the explicit unknown sentinel. */
export const ResolvedPaymentStatus = z.union([
  PaymentStatus,
  z.literal(UNKNOWN_PAYMENT_STATUS),
]);
export type ResolvedPaymentStatusT = z.infer<typeof ResolvedPaymentStatus>;

/**
 * Narrow a raw status without guessing. `completed` would report money that
 * never arrived; `failed` would hide money that did. Cross-service rows go
 * through here because they are written under a different service's
 * constraint, which can add a state this build has never seen.
 */
export const resolvePaymentStatus = (value: unknown): ResolvedPaymentStatusT => {
  const parsed = PaymentStatus.safeParse(value);
  return parsed.success ? parsed.data : UNKNOWN_PAYMENT_STATUS;
};

/** Who kicked the payment off — null on system/loan-driven rows. */
export const PaymentInitiator = z.enum(['customer', 'agent']);
export type PaymentInitiatorT = z.infer<typeof PaymentInitiator>;

/** Read-only reversal and ZaadCredit-correction state; optional so fixtures without it still type. */
export const PaymentReversalFields = z.object({
  reversedAt: z.string().nullable().optional(),
  reversedByName: z.string().nullable().optional(),
  reversalReason: z.string().nullable().optional(),
  /** ZaadCredit already received this payment before it was reversed. */
  zaadReported: z.boolean().optional(),
  zaadCorrectionRequired: z.boolean().optional(),
  zaadCorrectionResolvedAt: z.string().nullable().optional(),
  zaadCorrectionResolvedByName: z.string().nullable().optional(),
  zaadCorrectionNote: z.string().nullable().optional(),
});
export type PaymentReversalFieldsModel = z.infer<typeof PaymentReversalFields>;

export const Payment = PaymentReversalFields.extend({
  id: z.string(),
  contractId: z.string(),
  contractNumber: z.string(),
  customerName: z.string(),
  amount: z.number(),
  type: ResolvedPaymentType,
  status: PaymentStatus,
  method: PaymentMethod,
  providerTransactionId: z.string().nullable(),
  /** The wallet issuer's own reference for the money, e.g. Telesom's Zaad id. */
  issuerTransactionId: z.string().nullable(),
  payerMsisdn: z.string().nullable(),
  initiator: PaymentInitiator.nullable(),
  paidAt: z.string().nullable(),
  failureReason: z.string().nullable(),
  note: z.string().nullable(),
  recordedBy: z.string(),
  createdAt: z.string(),
});
export type PaymentModel = z.infer<typeof Payment>;

export const PaymentListItem = PaymentReversalFields.extend({
  id: z.string(),
  /**
   * Contract UUID, or `null` on application-only (pre-contract) payments.
   * Doubles as the `/contracts/$contractId` route param, so it must stay a
   * raw identifier — never a display string.
   */
  contractId: z.string().nullable(),
  /**
   * Application UUID. Present on upfront money and the only identifier a
   * pre-contract payment has, so the list can name it instead of rendering a
   * blank cell.
   */
  applicationId: z.string().nullable(),
  contractNumber: z.string(),
  customerName: z.string(),
  amount: z.number(),
  type: ResolvedPaymentType,
  status: PaymentStatus,
  method: PaymentMethod,
  providerTransactionId: z.string().nullable(),
  /** The wallet issuer's own reference for the money, e.g. Telesom's Zaad id. */
  issuerTransactionId: z.string().nullable(),
  /** Why a failed payment failed, as the refusing service put it. */
  failureReason: z.string().nullable(),
  createdAt: z.string(),
});
export type PaymentListItemModel = z.infer<typeof PaymentListItem>;

/**
 * Methods accepted by the backend for creating a payment. This is the
 * canonical write-side set (the read-side `PaymentMethod` keeps legacy
 * values like `mobile_money` for back-compat parsing/filtering).
 */
export const RecordPaymentMethod = z.enum([
  'cash',
  'evc_plus',
  'zaad',
  'sahal',
  'mpesa',
]);
export type RecordPaymentMethodType = z.infer<typeof RecordPaymentMethod>;

/**
 * The mobile-money rails, and which of them can actually be charged today.
 *
 * Only Zaad is live in production. The other three stay in the catalog because
 * the read side still needs them — a payment taken on EVC Plus before it was
 * withdrawn must keep rendering as "EVC Plus" in the ledger, not as a raw
 * `evc_plus` — but `enabled` gates every place an operator *picks* a rail.
 * Offering a dead rail hands the agent a prompt that fails at the operator,
 * with the customer standing at the counter.
 *
 * Bringing a rail back is one line: flip `enabled` and the push dialog, the
 * Record Payment form and the payments filter all pick it up, with the push
 * dialog reverting from the stated single rail to a grid of choices.
 */
export const PaymentRail = z.enum(['evc_plus', 'zaad', 'sahal', 'mpesa']);
export type PaymentRailType = z.infer<typeof PaymentRail>;

export type PaymentRailDescriptor = {
  value: PaymentRailType;
  /** Wallet name. A proper noun — never translated. */
  label: string;
  /** The mobile network that runs the wallet. */
  operator: string;
  /** Live in production and safe to offer. */
  enabled: boolean;
};

export const PAYMENT_RAILS: readonly PaymentRailDescriptor[] = [
  { value: 'zaad', label: 'Zaad', operator: 'Telesom', enabled: true },
  { value: 'evc_plus', label: 'EVC Plus', operator: 'Hormuud', enabled: false },
  { value: 'sahal', label: 'Sahal', operator: 'Golis', enabled: false },
  { value: 'mpesa', label: 'M-Pesa', operator: 'Safaricom', enabled: false },
];

/** Rails an operator may choose, in display order. */
export const ENABLED_PAYMENT_RAILS: readonly PaymentRailDescriptor[] =
  PAYMENT_RAILS.filter((r) => r.enabled);

/**
 * The one live rail, or `null` when several are. Pickers branch on this: one
 * rail is a fact to state, several are a choice to offer.
 */
export const SOLE_PAYMENT_RAIL: PaymentRailDescriptor | null =
  ENABLED_PAYMENT_RAILS.length === 1 ? ENABLED_PAYMENT_RAILS[0] : null;

/** Rail used where a flow charges without asking. */
export const DEFAULT_PAYMENT_RAIL: PaymentRailType =
  ENABLED_PAYMENT_RAILS[0]?.value ?? 'zaad';

/**
 * Methods the Record Payment form offers: cash, plus every live rail.
 *
 * Cash is deliberately not gated — it is money an agent physically took at the
 * counter, not an integration that can be down, so it stays recordable
 * regardless of which rails are live.
 */
export const RECORD_PAYMENT_OPTIONS: readonly {
  value: RecordPaymentMethodType;
  label: string | null;
  /** i18n key; rails carry their operator in `label` instead. */
  labelKey: string;
}[] = [
  { value: 'cash', label: null, labelKey: 'payments.method.cash' },
  ...ENABLED_PAYMENT_RAILS.map((r) => ({
    value: r.value,
    label: `${r.label} (${r.operator})`,
    labelKey: `payments.method.${r.value}`,
  })),
];

/**
 * Method values the payments list offers as filter chips: the live rails plus
 * the non-rail methods, which are recorded by hand and never went away.
 */
export const FILTERABLE_PAYMENT_METHODS: readonly PaymentMethodType[] = [
  ...ENABLED_PAYMENT_RAILS.map((r) => r.value),
  'cash',
  'bank_transfer',
];

export const RecordPayment = z.object({
  contractId: z.string(),
  amount: z.number().positive(),
  type: PaymentType,
  method: RecordPaymentMethod,
  providerTransactionId: z.string().optional(),
  note: z.string().optional(),
});
export type RecordPaymentModel = z.infer<typeof RecordPayment>;
